import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ExternalLink, Copy, Check, AlertTriangle, Smartphone, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const WEBVIEW_SIGNS = [
  "instagram", "fban", "fbav", "fb_iab", "fbios", "tiktok", "musical_ly",
  "bytedance", "aweme", "snapchat", "pinterest", "linkedinapp", "whatsapp",
  "twitter", "line/", "micromessenger", "telegram",
];

function detectClientWebview() {
  const ua = (navigator.userAgent || "").toLowerCase();
  const sign = WEBVIEW_SIGNS.find((s) => ua.includes(s));
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  return { isWebview: !!sign, isIOS, isAndroid };
}

export default function GoRedirect() {
  const { slug } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | webview | error
  const [target, setTarget] = useState("");
  const [countdown, setCountdown] = useState(3);
  const [copied, setCopied] = useState(false);
  const [client, setClient] = useState({ isWebview: false, isIOS: false, isAndroid: false });
  const timerRef = useRef(null);

  useEffect(() => {
    const c = detectClientWebview();
    setClient(c);
    const path = slug ? `/resolve/${slug}` : "/resolve";
    api
      .get(path)
      .then(({ data }) => {
        const url = data.route.target_url;
        setTarget(url);
        if (c.isWebview || data.detection.is_webview) {
          setState("webview");
        } else {
          setState("ready");
        }
      })
      .catch(() => setState("error"));
    return () => clearInterval(timerRef.current);
  }, [slug]);

  useEffect(() => {
    if (state !== "ready" || !target) return;
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          window.location.replace(target);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [state, target]);

  const forceNativeBrowser = () => {
    if (!target) return;
    if (client.isAndroid) {
      try {
        const clean = target.replace(/^https?:\/\//, "");
        window.location.href = `intent://${clean}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
        return;
      } catch {}
    }
    window.location.href = target;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(target);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg"
      >
        <div className="relative rounded-2xl border border-border bg-card/85 backdrop-blur-xl p-8 scanline overflow-hidden text-center">
          <div className="absolute top-0 left-0 h-0.5 w-full bg-gradient-to-r from-primary via-accent to-transparent" />

          <div className="flex items-center justify-center gap-2 font-mono-head font-extrabold mb-6">
            <span className="grid place-items-center h-8 w-8 rounded bg-primary text-primary-foreground glow-primary">
              <Zap size={18} />
            </span>
            FENIX<span className="text-primary">·</span>ZERO
          </div>

          {state === "loading" && (
            <div data-testid="redirect-loading" className="py-10 font-mono-head text-primary animate-pulse">
              RESOLVING ROUTE…
            </div>
          )}

          {state === "error" && (
            <div data-testid="redirect-error" className="py-8">
              <AlertTriangle className="mx-auto text-destructive mb-3" size={40} />
              <div className="font-mono-head font-bold text-lg">Route Not Found</div>
              <p className="text-sm text-muted-foreground mt-2">
                This deep link is disabled or does not exist.
              </p>
            </div>
          )}

          {state === "ready" && (
            <div data-testid="redirect-ready" className="py-4">
              <div className="text-emerald-400 font-mono-head text-sm uppercase tracking-widest">
                Secure Redirect · HTTP 307
              </div>
              <div className="my-6">
                <div className="text-5xl font-mono-head font-extrabold text-primary text-glow">{countdown}</div>
                <div className="text-xs text-muted-foreground mt-2">Opening your destination…</div>
              </div>
              <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-primary"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 3, ease: "linear" }}
                />
              </div>
              <button
                data-testid="redirect-now-btn"
                onClick={() => window.location.replace(target)}
                className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono-head font-semibold px-6 py-3 rounded-md hover:bg-[#E04800] transition-colors glow-primary"
              >
                Continue Now <ArrowRight size={18} />
              </button>
            </div>
          )}

          {state === "webview" && (
            <div data-testid="redirect-webview" className="py-2 text-left">
              <div className="flex items-center gap-2 justify-center text-[#EC4899] font-mono-head text-sm uppercase tracking-widest mb-4">
                <Smartphone size={16} /> In-App Browser Detected
              </div>
              <p className="text-sm text-muted-foreground text-center mb-6">
                You're inside an app's built-in browser. For this link to work
                correctly, open it in your <b className="text-foreground">default browser</b>.
              </p>

              <button
                data-testid="open-native-browser-btn"
                onClick={forceNativeBrowser}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono-head font-semibold py-3 rounded-md hover:bg-[#E04800] transition-colors glow-primary mb-4"
              >
                <ExternalLink size={18} /> Open in Browser
              </button>

              <div className="rounded-lg border border-border bg-background/60 p-4 space-y-2 text-sm">
                <div className="font-mono-head text-xs uppercase tracking-widest text-accent mb-1">
                  Manual steps
                </div>
                {client.isIOS ? (
                  <ol className="list-decimal list-inside text-muted-foreground space-y-1">
                    <li>Tap the <b className="text-foreground">•••</b> menu (top-right)</li>
                    <li>Choose <b className="text-foreground">Open in Safari / Browser</b></li>
                  </ol>
                ) : (
                  <ol className="list-decimal list-inside text-muted-foreground space-y-1">
                    <li>Tap the <b className="text-foreground">⋮</b> menu (top-right)</li>
                    <li>Choose <b className="text-foreground">Open in Chrome / Browser</b></li>
                  </ol>
                )}
              </div>

              <button
                data-testid="copy-link-btn"
                onClick={copyLink}
                className="mt-4 w-full flex items-center justify-center gap-2 border border-border py-2.5 rounded-md font-mono-head text-sm hover:border-accent hover:text-accent transition-colors"
              >
                {copied ? <><Check size={16} /> Link Copied</> : <><Copy size={16} /> Copy Destination Link</>}
              </button>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-border text-[11px] font-code text-muted-foreground">
            No third-party tracking · powered by Fenix Zero-Click
          </div>
        </div>
      </motion.div>
    </div>
  );
}
